import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePlans } from '@/hooks/useData';
import { RefreshCw, Check, Wifi } from 'lucide-react';
import { useThemeStore } from '@/stores';

interface Plan {
  id: string;
  name: string;
  speed: number;
  price: number;
  description?: string;
  features?: string | string[];
  isActive?: boolean;
  imageUrl?: string;
  title?: string;
  isTod?: boolean;
}

// Fallback data matching the actual FTTH National Project API structure
const fallbackPlans: Plan[] = [
  {
    id: 'BASIC',
    name: 'BASIC',
    speed: 60,
    price: 35000,
    description: 'سرعتك صارت 60.. وبنفس السعر، استمتع بتصفح فائق وتنزيل سلس',
    features: ['BASIC', 'IPTV', 'Parental Control'],
    isActive: true,
    imageUrl: '/svgexport.png',
    isTod: false,
  },
  {
    id: 'PLUS',
    name: 'PLUS',
    speed: 125,
    price: 45000,
    description: 'سرعتك صارت 125.. وبنفس السعر، لتحميل فوري وقوي للملفات والفيديوهات',
    features: ['PLUS', 'IPTV', 'Parental Control'],
    isActive: true,
    imageUrl: '/svgexport.png',
    isTod: false,
  },
  {
    id: 'TURBO',
    name: 'TURBO',
    speed: 250,
    price: 65000,
    description: 'سرعتك صارت 250.. وبنفس السعر، إنترنت فائق السرعة واستقرار عالي للألعاب والعائلة',
    features: ['TURBO', 'IPTV', 'Parental Control'],
    isActive: true,
    imageUrl: '/svgexport.png',
    isTod: false,
  },
  {
    id: 'PRO MAX',
    name: 'PRO MAX',
    speed: 500,
    price: 100000,
    description: 'سرعتك صارت 500.. وبنفس السعر، السرعة القصوى للأداء الاحترافي والتحميل المكثف',
    features: ['PRO MAX', 'IPTV', 'Parental Control'],
    isActive: true,
    imageUrl: '/svgexport.png',
    isTod: false,
  },
];

const unwrapResponse = (res: any): any => {
  if (!res) return null;
  let data = res;
  while (data && typeof data === 'object' && 'data' in data && !('items' in data) && !('applicableSubscriptions' in data)) {
    data = data.data;
  }
  return data;
};

const ServicesPlansPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: rawPlans, isLoading, isFetching, refetch } = usePlans();
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  // Handle plan details formatting safely
  const plansList: Plan[] = React.useMemo(() => {
    if (!rawPlans) return fallbackPlans;

    const unwrapped = unwrapResponse(rawPlans);

    // The API returns: { totalCount: 1, items: [{ price, description, applicableSubscriptions: [...] }] }
    let subscriptions: any[] = [];

    if (unwrapped?.items && Array.isArray(unwrapped.items)) {
      // Collect all applicableSubscriptions from all bundle items
      for (const bundleItem of unwrapped.items) {
        if (Array.isArray(bundleItem.applicableSubscriptions)) {
          subscriptions = [...subscriptions, ...bundleItem.applicableSubscriptions];
        }
      }
    } else if (Array.isArray(unwrapped)) {
      // Fallback: flat array
      subscriptions = unwrapped;
    }

    if (subscriptions.length === 0) return fallbackPlans;

    // Robust dictionary of official FTTH and TOD plan prices
    const planPrices: Record<string, number> = {
      'BASIC': 35000,
      'PLUS': 45000,
      'TURBO': 65000,
      'PRO MAX': 100000,
      'PRO_MAX': 100000,
      'PROMAX': 100000,

      // Fiber naming
      'FIBER 35': 35000,
      'FIBER 50': 45000,
      'FIBER 75': 65000,
      'FIBER 150': 100000,

      // TOD plans
      'TOD 35': 85000,
      'TOD 50': 90000,
      'TOD 75': 95000,
      'TOD 150': 119000,
      'TOD BASIC': 85000,
      'TOD PLUS': 90000,
      'TOD TURBO': 95000,
      'TOD PRO MAX': 119000,
    };

    return subscriptions.map((sub: any) => {
      // Parse speed number from speed string like "60 Mbps"
      let speed = 60;
      if (sub.speed) {
        const match = sub.speed.match(/(\d+)/);
        if (match) speed = parseInt(match[1], 10);
      }

      const features = Array.isArray(sub.variants)
        ? sub.variants.map((v: any) => v.displayValue || v.id)
        : ['IPTV', 'Parental Control'];

      // Detect TOD plan by plan name or bundleId
      const isTod = (sub.planName || '').includes('TOD') ||
                    (sub.basePlanId || '').includes('TOD') ||
                    (sub.bundleId || '').includes('TOD');

      const planKey = (sub.planName || sub.basePlanId || sub.name || '').toUpperCase().trim();

      let resolvedPrice = 0;
      if (sub.price && typeof sub.price.value === 'number' && sub.price.value > 0) {
        resolvedPrice = sub.price.value;
      } else if (sub.billingPrice && typeof sub.billingPrice.value === 'number' && sub.billingPrice.value > 0) {
        resolvedPrice = sub.billingPrice.value;
      } else if (typeof sub.price === 'number' && sub.price > 0) {
        resolvedPrice = sub.price;
      } else if (planPrices[planKey]) {
        resolvedPrice = planPrices[planKey];
      } else if (speed >= 500) {
        resolvedPrice = 100000;
      } else if (speed >= 250) {
        resolvedPrice = 65000;
      } else if (speed >= 125) {
        resolvedPrice = 45000;
      } else {
        resolvedPrice = 35000;
      }

      return {
        id: sub.basePlanId || sub.planName || Math.random().toString(),
        name: sub.planName || sub.title || 'FIBER PLAN',
        speed: speed,
        price: resolvedPrice,
        description: sub.description || sub.title || '',
        features: features,
        isActive: true,
        // Use local images: /svgexport.png for FTTH plans, /tod_logo.svg for TOD plans
        imageUrl: isTod ? '/tod_logo.svg' : '/svgexport.png',
        title: sub.title,
        isTod: isTod,
      };
    });
  }, [rawPlans]);

  return (
    <div style={{ padding: '20px', direction: 'rtl', minHeight: '100%' }}>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '28px',
        }}
      >
        <div style={{ textAlign: 'right' }}>
          <h1 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>
            {t('servicesPlans.title') || 'الخدمات والخطط الحالية'}
          </h1>
          <p style={{ color: isLight ? '#475569' : '#64748b', fontSize: '13px' }}>
            قائمة بحزم اشتراكات الألياف الضوئية المعتمدة في المشروع الوطني العراقي
          </p>
        </div>
        <button
          onClick={() => refetch()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`,
            borderRadius: '8px',
            padding: '8px 14px',
            color: isLight ? '#475569' : '#94a3b8',
            cursor: 'pointer',
            fontSize: '13px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)';
            e.currentTarget.style.color = isLight ? '#0f172a' : '#fff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)';
            e.currentTarget.style.color = isLight ? '#475569' : '#94a3b8';
          }}
        >
          <RefreshCw size={14} style={isFetching ? { animation: 'spin 1s linear infinite' } : {}} />
          {t('common.refresh') || 'تحديث'}
        </button>
      </div>

      {/* Grid of Plans */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <RefreshCw size={32} style={{ animation: 'spin 1.2s linear infinite', color: '#3b82f6' }} />
            <span style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '14px' }}>جاري تحميل الحزم والسرعات المتاحة...</span>
          </div>
        </div>
      ) : (
        <div
          className="plans-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
          }}
        >
          {plansList.map((plan) => {
            const isPopular = plan.speed === 60 || plan.name.includes('50');
            const isTod = plan.isTod || plan.name.includes('TOD');

            const accentColor =
              isTod ? '#f97316' :
              plan.speed <= 50 ? '#3b82f6' :
              plan.speed <= 90 ? '#a855f7' :
              '#0d9488';

            const borderStyle = isPopular
              ? `2.5px solid ${accentColor}`
              : (isLight ? `2px solid ${accentColor}B3` : `1.5px solid ${accentColor}4D`);

            const shadowStyle = isPopular
              ? (isLight ? `0 6px 24px rgba(168, 85, 247, 0.2)` : `0 0 20px rgba(168, 85, 247, 0.25)`)
              : (isLight ? `0 4px 12px ${accentColor}15` : 'none');

            return (
              <div
                key={plan.id}
                style={{
                  background: isLight ? '#ffffff' : 'rgba(13,31,60,0.6)',
                  border: borderStyle,
                  borderRadius: '16px',
                  padding: '24px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: shadowStyle,
                }}
                className="plan-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.borderColor = accentColor;
                  e.currentTarget.style.background = isLight ? '#fafafa' : 'rgba(13,31,60,0.85)';
                  if (isPopular) {
                    e.currentTarget.style.boxShadow = isLight ? `0 10px 25px rgba(168, 85, 247, 0.2)` : `0 10px 30px rgba(168, 85, 247, 0.4)`;
                  } else {
                    e.currentTarget.style.boxShadow = isLight ? `0 10px 20px rgba(0,0,0,0.05)` : `0 10px 20px ${accentColor}1A`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = isPopular ? accentColor : (isLight ? `${accentColor}B3` : `${accentColor}4D`);
                  e.currentTarget.style.background = isLight ? '#ffffff' : 'rgba(13,31,60,0.6)';
                  e.currentTarget.style.boxShadow = shadowStyle;
                }}
              >
                {/* Popular / Special Tag */}
                {(isPopular || isTod) && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-14px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: isTod
                        ? 'linear-gradient(90deg, #f97316, #eab308)'
                        : 'linear-gradient(90deg, #ec4899, #a855f7)',
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '4px 14px',
                      borderRadius: '999px',
                      boxShadow: isTod
                        ? '0 0 12px rgba(249,115,22,0.5)'
                        : '0 0 12px rgba(168,85,247,0.5)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isTod ? 'باقة كأس العالم الرياضية' : 'الحزمة الأكثر طلباً'}
                  </span>
                )}

                <div>
                  {/* Header info */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', direction: 'rtl' }}>
                    {/* Plan Logo / Avatar */}
                    <div
                      style={{
                        width: 'auto',
                        minWidth: '44px',
                        height: '44px',
                        borderRadius: '12px',
                        background: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2px',
                        overflow: 'hidden',
                      }}
                    >
                      {plan.imageUrl ? (
                        <img
                          src={plan.imageUrl}
                          alt={plan.name}
                          style={{
                            height: '100%',
                            maxWidth: '110px',
                            objectFit: 'contain',
                          }}
                          onError={(e) => {
                            // Fallback to icon if image fails to load
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                            const parent = (e.currentTarget as HTMLImageElement).parentElement;
                            if (parent) {
                              parent.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="${accentColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>`;
                            }
                          }}
                        />
                      ) : (
                        <Wifi size={28} color={accentColor} />
                      )}
                    </div>
                    <span
                      style={{
                        background: isTod ? 'rgba(249,115,22,0.1)' : 'rgba(16,185,129,0.1)',
                        color: isTod ? '#f97316' : '#10b981',
                        border: isTod ? '1px solid rgba(249,115,22,0.25)' : '1px solid rgba(16,185,129,0.25)',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isTod ? 'شامل اشتراك TOD' : 'نشطة ومتوفرة'}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <h3 style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '18px', fontWeight: 800, marginBottom: '8px', textAlign: 'right' }}>
                    {plan.name}
                  </h3>
                  <p style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '13px', lineHeight: 1.6, marginBottom: '20px', textAlign: 'right', minHeight: '60px' }}>
                    {plan.description}
                  </p>

                  {/* Speed Banner */}
                  <div
                    style={{
                      background: isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(5, 15, 30, 0.4)',
                      border: `1px solid ${accentColor}33`,
                      borderRadius: '12px',
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '20px',
                      direction: 'rtl',
                    }}
                  >
                    <span style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '13px' }}>سرعة نقل البيانات:</span>
                    <div
                      style={{
                        background: `${accentColor}1F`,
                        border: `1px solid ${accentColor}33`,
                        borderRadius: '8px',
                        padding: '6px 12px',
                        color: isLight ? '#1e293b' : '#f1f5f9',
                        fontSize: '14px',
                        fontWeight: 700,
                        direction: 'ltr',
                      }}
                    >
                      Mbps {plan.speed}
                    </div>
                  </div>
                  {/* Pricing */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '24px', justifyContent: 'flex-start', direction: 'rtl' }}>
                    <span style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '32px', fontWeight: 800 }}>
                      {plan.price.toLocaleString('en-US')}
                    </span>
                    <span style={{ color: '#64748b', fontSize: '14px' }}>دينار / شهر</span>
                  </div>

                  {/* Separator */}
                  <div style={{ height: '1px', background: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)', marginBottom: '20px' }} />

                  {/* Features */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', direction: 'rtl' }}>
                    {Array.isArray(plan.features) &&
                      plan.features.map((feature, i) => (
                        <li key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', color: isLight ? '#475569' : '#94a3b8', fontSize: '13px', justifyContent: 'flex-start' }}>
                          <Check size={14} color={accentColor} style={{ flexShrink: 0 }} />
                          <span>{feature}</span>
                        </li>
                      ))}
                  </ul>
                </div>

                {/* Footer Action */}
                <div style={{ marginTop: '24px' }}>
                  <button
                    disabled={!plan.isActive}
                    style={{
                      width: '100%',
                      padding: '11px 0',
                      borderRadius: '10px',
                      background: isPopular || isTod
                        ? `linear-gradient(90deg, ${accentColor}, #ec4899)`
                        : 'transparent',
                      border: `1px solid ${isPopular || isTod ? 'transparent' : accentColor}`,
                      color: isPopular || isTod ? '#fff' : (isLight ? accentColor : '#fff'),
                      fontWeight: 600,
                      fontSize: '14px',
                      cursor: plan.isActive ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (plan.isActive) {
                        if (isPopular || isTod) {
                          e.currentTarget.style.opacity = '0.9';
                        } else {
                          e.currentTarget.style.background = `${accentColor}1A`;
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (plan.isActive) {
                        if (isPopular || isTod) {
                          e.currentTarget.style.opacity = '1';
                        } else {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }
                    }}
                  >
                    تفعيل واشتراك للمشتركين
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ServicesPlansPage;
